import React, { useMemo, useState } from 'react';
import { Grid, Paper, Typography, Box, Stack, Button, Tooltip } from '@mui/material';
import { motion } from 'framer-motion';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { feedbackHeroTokens } from './feedbackHeroTokens';

const DEFAULT_LIMIT = 8;

const CountChip = ({ value, tone, Icon }) => {
  const { fonts } = feedbackHeroTokens;
  return (
    <Stack
      direction="row"
      spacing={0.25}
      alignItems="center"
      sx={{
        px: 0.65,
        py: 0.15,
        borderRadius: 4,
        background: tone.bg,
        border: `1px solid ${tone.chip}`,
      }}
    >
      <Icon sx={{ fontSize: 11, color: tone.fg }} />
      <Typography
        sx={{
          fontFamily: fonts.display,
          fontWeight: 800,
          fontSize: '0.78rem',
          color: tone.fg,
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
};

const DivergingBar = ({ positivo, negativo, maxValue, colors }) => {
  const posPct = maxValue > 0 ? (positivo / maxValue) * 50 : 0;
  const negPct = maxValue > 0 ? (negativo / maxValue) * 50 : 0;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: 10,
        borderRadius: 6,
        background: colors.surfaceAlt,
        border: `1px solid ${colors.softBorder}`,
        overflow: 'hidden',
      }}
    >
      {/* Center divider */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: '50%',
          width: '1px',
          background: 'rgba(15, 23, 42, 0.18)',
          zIndex: 1,
        }}
      />
      {/* Negative bar - grows left from center */}
      {negativo > 0 && (
        <Box
          className="ponto-bar-neg"
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: '50%',
            width: `${negPct}%`,
            background: `linear-gradient(270deg, ${colors.neg.accent} 0%, ${colors.neg.accent}AA 100%)`,
            borderRadius: '6px 0 0 6px',
            transition: 'width 0.7s cubic-bezier(.2,.8,.2,1), filter .25s ease',
          }}
        />
      )}
      {/* Positive bar - grows right from center */}
      {positivo > 0 && (
        <Box
          className="ponto-bar-pos"
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: '50%',
            width: `${posPct}%`,
            background: `linear-gradient(90deg, ${colors.pos.accent} 0%, ${colors.pos.accent}AA 100%)`,
            borderRadius: '0 6px 6px 0',
            transition: 'width 0.7s cubic-bezier(.2,.8,.2,1), filter .25s ease',
          }}
        />
      )}
    </Box>
  );
};

const PontoRow = ({ name, positivo, negativo, maxValue, colors, fonts }) => {
  const saldo = positivo - negativo;
  const dominantTone = saldo >= 0 ? colors.pos : colors.neg;
  const total = positivo + negativo;
  const sentimentoLabel =
    saldo > 0 ? 'Tema majoritariamente positivo' :
    saldo < 0 ? 'Tema majoritariamente negativo' :
    'Tema balanceado';

  const tooltipTitle = (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>{name}</Typography>
      <Typography sx={{ fontSize: '0.74rem', opacity: 0.85, mb: 0.5 }}>
        {sentimentoLabel}
      </Typography>
      <Stack direction="row" spacing={1.5}>
        <Typography sx={{ fontSize: '0.74rem' }}>
          <span style={{ color: '#86efac' }}>● Positivas:</span> <b>{positivo}</b>
        </Typography>
        <Typography sx={{ fontSize: '0.74rem' }}>
          <span style={{ color: '#fca5a5' }}>● Negativas:</span> <b>{negativo}</b>
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: '0.72rem', opacity: 0.75, mt: 0.5 }}>
        Total de menções: {total} · Saldo: {saldo > 0 ? '+' : ''}{saldo}
      </Typography>
    </Box>
  );

  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
      <Box
        sx={{
          py: 0.5,
          px: 1,
          mx: -1,
          borderRadius: 1.5,
          cursor: 'pointer',
          transition: 'background .25s ease, transform .25s ease, box-shadow .25s ease',
          '&:hover': {
            background: `${dominantTone.bg}55`,
            transform: 'translateX(3px)',
            boxShadow: `0 4px 12px ${dominantTone.accent}1F`,
          },
          '&:hover .ponto-bar-pos': {
            filter: 'brightness(1.08) saturate(1.15)',
          },
          '&:hover .ponto-bar-neg': {
            filter: 'brightness(1.08) saturate(1.15)',
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.6} spacing={1}>
          <Typography
            sx={{
              fontFamily: fonts.body,
              fontWeight: 600,
              fontSize: '0.86rem',
              color: colors.text,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              borderLeft: `3px solid ${dominantTone.accent}`,
              pl: 1,
            }}
          >
            {name}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: '0 0 auto' }}>
            {negativo > 0 && (
              <CountChip value={negativo} tone={colors.neg} Icon={ArrowDownwardIcon} />
            )}
            {positivo > 0 && (
              <CountChip value={positivo} tone={colors.pos} Icon={ArrowUpwardIcon} />
            )}
          </Stack>
        </Stack>
        <DivergingBar positivo={positivo} negativo={negativo} maxValue={maxValue} colors={colors} />
      </Box>
    </Tooltip>
  );
};

const FeedbackPointsChart = ({ feedbacks = [], pontos = null, half = false, googleExcluded = false }) => {
  const { fonts, colors } = feedbackHeroTokens;
  const [expanded, setExpanded] = useState(false);

  const allData = useMemo(() => {
    let rows = [];
    if (Array.isArray(pontos)) {
      rows = pontos.map((item) => ({
        categoria: item.categoria,
        positivo: item.positivo || 0,
        negativo: item.negativo || 0,
      }));
    } else {
      const counts = {};
      feedbacks.forEach((fb) => {
        fb.classificacoes?.forEach((c) => {
          const nome = c.categoria?.nome;
          if (!nome) return;
          if (!counts[nome]) counts[nome] = { positivo: 0, negativo: 0 };
          if (c.sentimento === 'positivo') counts[nome].positivo += 1;
          if (c.sentimento === 'negativo') counts[nome].negativo += 1;
        });
      });
      rows = Object.entries(counts).map(([categoria, v]) => ({
        categoria,
        positivo: v.positivo,
        negativo: v.negativo,
      }));
    }
    return rows
      .filter((r) => r.positivo > 0 || r.negativo > 0)
      .sort((a, b) => (b.positivo + b.negativo) - (a.positivo + a.negativo));
  }, [feedbacks, pontos]);

  const data = expanded ? allData : allData.slice(0, DEFAULT_LIMIT);
  const maxValue = Math.max(
    1,
    ...allData.map((d) => Math.max(d.positivo, d.negativo))
  );

  const totalPos = allData.reduce((acc, d) => acc + d.positivo, 0);
  const totalNeg = allData.reduce((acc, d) => acc + d.negativo, 0);
  const hasMore = allData.length > DEFAULT_LIMIT;

  return (
    <Grid item xs={12} md={half ? 6 : 12}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{ height: '100%' }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: { xs: 2, sm: 3 },
            background: colors.surface,
            border: `1px solid ${colors.softBorder}`,
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center" mb={0.5}>
            <InsightsOutlinedIcon sx={{ fontSize: 20, color: colors.text }} />
            <Typography
              sx={{
                fontFamily: fonts.display,
                fontWeight: 700,
                color: colors.text,
                fontSize: { xs: '1.05rem', sm: '1.15rem' },
              }}
            >
              Temas mais comentados
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            sx={{
              fontFamily: fonts.body,
              color: colors.subtext,
              display: 'block',
              mb: 2,
              fontSize: '0.82rem',
            }}
          >
            {googleExcluded
              ? 'Análise de temas das avaliações salvas. Avaliações do Google não entram nesta análise.'
              : 'O que mais aparece nos comentários, por sentimento.'}
          </Typography>

          {data.length === 0 ? (
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                border: `1px dashed ${colors.softBorder}`,
                background: colors.surfaceAlt,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontFamily: fonts.body, color: colors.subtext, fontSize: '0.88rem' }}>
                Sem temas categorizados neste período.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Legend */}
              <Stack direction="row" spacing={2} mb={1.5} alignItems="center" flexWrap="wrap">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colors.neg.accent }} />
                  <Typography
                    sx={{
                      fontFamily: fonts.body,
                      fontSize: '0.74rem',
                      fontWeight: 600,
                      color: colors.subtext,
                    }}
                  >
                    Menções negativas
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: fonts.display,
                      fontSize: '0.78rem',
                      fontWeight: 800,
                      color: colors.neg.fg,
                    }}
                  >
                    {totalNeg}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colors.pos.accent }} />
                  <Typography
                    sx={{
                      fontFamily: fonts.body,
                      fontSize: '0.74rem',
                      fontWeight: 600,
                      color: colors.subtext,
                    }}
                  >
                    Menções positivas
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: fonts.display,
                      fontSize: '0.78rem',
                      fontWeight: 800,
                      color: colors.pos.fg,
                    }}
                  >
                    {totalPos}
                  </Typography>
                </Stack>
              </Stack>

              <Stack spacing={1.25} sx={{ flex: 1 }}>
                {data.map((row) => (
                  <PontoRow
                    key={row.categoria}
                    name={row.categoria}
                    positivo={row.positivo}
                    negativo={row.negativo}
                    maxValue={maxValue}
                    colors={colors}
                    fonts={fonts}
                  />
                ))}
              </Stack>

              {hasMore && (
                <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                  <Button
                    size="small"
                    onClick={() => setExpanded((v) => !v)}
                    sx={{
                      fontFamily: fonts.body,
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      color: colors.subtext,
                    }}
                  >
                    {expanded
                      ? 'Mostrar apenas os principais'
                      : `Ver todos (${allData.length} temas)`}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </motion.div>
    </Grid>
  );
};

export default FeedbackPointsChart;
